import styles from './SplitLineConfig.module.scss'
import { useCallback, useEffect, useState } from 'react'
import { propExistsOnObject } from '../../utils'
import {
    AXIS_DEFAULT_VALUES,
    CHART_PREFERENCES_DEFAULT_VALUES,
    LINE_STYLES,
} from '../../styleConsts'
import {
    ScoopSlider,
    ScoopSelector,
    ScoopText,
    ScoopSwitch,
    ScoopColorPicker,
} from '../../../../../../ui'
import { debounce, cloneDeep } from 'lodash'
import styleTabStyles from '../../StyleTab.module.scss'

export const SplitLineConfig = ({ config, setConfig, chartPreferences }) => {
    const [xLineWidth, setXLineWidth] = useState(AXIS_DEFAULT_VALUES.splitLine.lineStyle.width)
    const [yLineWidth, setYLineWidth] = useState(AXIS_DEFAULT_VALUES.splitLine.lineStyle.width)

    useEffect(() => {
        setXLineWidth(getAxisPropValue('width', 'lineStyle', 'xAxis'))
        setYLineWidth(getAxisPropValue('width', 'lineStyle', 'yAxis'))
    }, [config])

    const debouncedFunc = useCallback(
        debounce((prop, value, axisName) => {
            const newConfig = cloneDeep(config)
            newConfig.styleOverrides[axisName].forEach(
                (axis) => (axis.splitLine.lineStyle[prop] = value)
            )
            setConfig(newConfig)
        }, 200),
        [config]
    )

    const getAxisPropValue = (prop, subObject, axisName) => {
        const overrides = config.styleOverrides[axisName][0]
        const preferences = chartPreferences[axisName]
        switch (subObject) {
            case 'splitLine':
                if (propExistsOnObject(overrides.splitLine, prop)) return overrides.splitLine[prop]
                if (propExistsOnObject(preferences?.splitLine, prop))
                    return preferences.splitLine[prop]
                if (prop === 'show')
                    return CHART_PREFERENCES_DEFAULT_VALUES[axisName].splitLine[prop]
                return AXIS_DEFAULT_VALUES.splitLine[prop]
            case 'lineStyle':
                if (propExistsOnObject(overrides.splitLine?.lineStyle, prop))
                    return overrides.splitLine.lineStyle[prop]
                if (propExistsOnObject(preferences?.splitLine?.lineStyle, prop))
                    return preferences.splitLine.lineStyle[prop]
                return AXIS_DEFAULT_VALUES.splitLine.lineStyle[prop]
        }
    }

    const handleAxisPropValueChange = (prop, value, subObject, axisName) => {
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides[axisName].forEach((axis) => {
            switch (subObject) {
                case 'splitLine':
                    axis.splitLine[prop] = value
                    break
                case 'lineStyle':
                    axis.splitLine.lineStyle[prop] = value
                    break
            }
        })
        setConfig(newConfig)
    }

    const handleDebounceAxisPropValueChange = (prop, value, setter, axisName) => {
        debouncedFunc(prop, value, axisName)
        setter(value)
    }

    const showX = getAxisPropValue('show', 'splitLine', 'xAxis')
    const showY = getAxisPropValue('show', 'splitLine', 'yAxis')

    // Convert LINE_STYLES to options format for ScoopSelector
    const lineStyleOptions = LINE_STYLES.map((style) => ({
        value: style.value,
        label: style.label,
    }))

    return (
        <div className={styles.container}>
            <div className={styles.axisSection}>
                <ScoopText variant="label">X Axis</ScoopText>
                <div className={styleTabStyles.switchSection}>
                    <ScoopText className={styleTabStyles.switchLabel}>Show</ScoopText>
                    <ScoopSwitch
                        checked={showX}
                        onChange={() =>
                            handleAxisPropValueChange('show', !showX, 'splitLine', 'xAxis')
                        }
                    />
                </div>
                <ScoopSelector
                    label="Type"
                    value={getAxisPropValue('type', 'lineStyle', 'xAxis')}
                    onChange={(e) =>
                        handleAxisPropValueChange('type', e.target.value, 'lineStyle', 'xAxis')
                    }
                    options={lineStyleOptions}
                    size="md"
                    removeMargin
                />
                <ScoopSlider
                    label={`Line width: ${xLineWidth}px`}
                    value={xLineWidth}
                    onChange={(v) =>
                        handleDebounceAxisPropValueChange('width', v, setXLineWidth, 'xAxis')
                    }
                    min={0}
                    max={12}
                    step={1}
                />
                <div className={styles.colorPickerContainer}>
                    <div className={styles.colorPickerSection}>
                        <ScoopText variant="label">Line color</ScoopText>
                        <div className={styles.colorPickerPadding}>
                            <ScoopColorPicker
                                showLabel
                                value={getAxisPropValue('color', 'lineStyle', 'xAxis')}
                                onChange={(color) =>
                                    handleAxisPropValueChange('color', color, 'lineStyle', 'xAxis')
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.axisSectionWithBorder}>
                <ScoopText variant="label">Y Axis</ScoopText>
                <div className={styleTabStyles.switchSection}>
                    <ScoopText className={styleTabStyles.switchLabel}>Show</ScoopText>
                    <ScoopSwitch
                        checked={showY}
                        onChange={() =>
                            handleAxisPropValueChange('show', !showY, 'splitLine', 'yAxis')
                        }
                    />
                </div>
                <ScoopSelector
                    label="Type"
                    value={getAxisPropValue('type', 'lineStyle', 'yAxis')}
                    onChange={(e) =>
                        handleAxisPropValueChange('type', e.target.value, 'lineStyle', 'yAxis')
                    }
                    options={lineStyleOptions}
                    size="md"
                    removeMargin
                />
                <ScoopSlider
                    label={`Line width: ${yLineWidth}px`}
                    value={yLineWidth}
                    onChange={(v) =>
                        handleDebounceAxisPropValueChange('width', v, setYLineWidth, 'yAxis')
                    }
                    min={0}
                    max={12}
                    step={1}
                />
                <div className={styles.colorPickerContainer}>
                    <div className={styles.colorPickerSection}>
                        <ScoopText variant="label">Line color</ScoopText>
                        <div className={styles.colorPickerPadding}>
                            <ScoopColorPicker
                                showLabel
                                value={getAxisPropValue('color', 'lineStyle', 'yAxis')}
                                onChange={(color) =>
                                    handleAxisPropValueChange('color', color, 'lineStyle', 'yAxis')
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
